import React, { useState, useEffect } from 'react';
import { FaTimes, FaPlus, FaTrash } from 'react-icons/fa';

const QUESTION_TYPES = [
  { value: 'number', label: 'Number' },
  { value: 'text', label: 'Text' },
  { value: 'single_select', label: 'Single Select' },
  { value: 'multi_select', label: 'Multi Select' }
];

const HistoryQuestionsModal = ({ isOpen, onClose, onSave, question, mode }) => {
  const [formData, setFormData] = useState({
    question_text: '',
    question_type: 'text',
    description: '',
    options: [],
    is_required: true,
    position: 1,
    is_active: true
  });

  const [newOption, setNewOption] = useState({ label: '', value: '' });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (mode === 'edit' && question) {
      setFormData({
        question_text: question.question_text || '',
        question_type: question.question_type || 'text',
        description: question.description || '',
        options: question.options || [],
        is_required: question.is_required !== undefined ? question.is_required : true,
        position: question.position || 1,
        is_active: question.is_active !== undefined ? question.is_active : true
      });
    } else {
      setFormData({
        question_text: '',
        question_type: 'text',
        description: '',
        options: [],
        is_required: true,
        position: 1,
        is_active: true
      });
    }
    setNewOption({ label: '', value: '' });
    setErrors({});
  }, [mode, question, isOpen]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleAddOption = () => {
    if (!newOption.label.trim()) {
      alert('Please enter an option value');
      return;
    }

    const optionExists = formData.options.some(opt => opt.value === newOption.value);
    if (optionExists) {
      alert('This option already exists');
      return;
    }

    setFormData(prev => ({
      ...prev,
      options: [...prev.options, { label: newOption.label, value: newOption.value, id: Date.now() }]
    }));
    setNewOption({ label: '', value: '' });
  };

  const handleRemoveOption = (optionId) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter(opt => opt.id !== optionId)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.question_text.trim()) {
      newErrors.question_text = 'Question text is required';
    }

    if ((formData.question_type === 'single_select' || formData.question_type === 'multi_select') && formData.options.length === 0) {
      newErrors.options = 'At least one option is required for select questions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-orange-700 text-white px-6 py-4 flex justify-between items-center border-b">
          <h2 className="text-xl font-bold">
            {mode === 'create' ? '+ Add New Question' : '✎ Edit Question'}
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:bg-orange-800 p-2 rounded-lg transition-colors"
          >
            <FaTimes size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Question Text */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Question Text <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="question_text"
              value={formData.question_text}
              onChange={handleInputChange}
              placeholder="Enter the question"
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                errors.question_text ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.question_text && (
              <p className="text-red-500 text-sm mt-1">{errors.question_text}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Optional description or hint for the question"
              rows="2"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>

          {/* Question Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Question Type <span className="text-red-500">*</span>
            </label>
            <select
              name="question_type"
              value={formData.question_type}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              {QUESTION_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Options for Select Questions */}
          {(formData.question_type === 'single_select' || formData.question_type === 'multi_select') && (
            <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Options <span className="text-red-500">*</span>
              </label>

              {/* Add Option */}
              <div className="space-y-3 mb-4">
                <input
                  type="text"
                  value={newOption.label}
                  onChange={(e) => setNewOption(prev => ({ ...prev, label: e.target.value, value: e.target.value }))}
                  placeholder="Option value (e.g., 'Male')"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                <button
                  type="button"
                  onClick={handleAddOption}
                  className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors flex items-center justify-center gap-2"
                >
                  <FaPlus size={14} /> Add Option
                </button>
              </div>

              {errors.options && (
                <p className="text-red-500 text-sm mb-3">{errors.options}</p>
              )}

              {/* Options List */}
              <div className="space-y-2">
                {formData.options.length === 0 ? (
                  <p className="text-gray-500 text-sm italic">No options added yet</p>
                ) : (
                  formData.options.map((option, idx) => (
                    <div
                      key={option.id}
                      className="flex items-center justify-between bg-white p-3 rounded-lg border border-gray-200 hover:border-orange-300 transition-colors"
                    >
                      <p className="font-medium text-gray-900">{option.label}</p>
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(option.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Position */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Position <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="position"
                value={formData.position}
                onChange={handleInputChange}
                min="1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* Required */}
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_required"
                  checked={formData.is_required}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-sm font-semibold text-gray-700">Required Field</span>
              </label>
            </div>
          </div>

          {/* Active Status */}
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="w-4 h-4 text-green-600 rounded focus:ring-2 focus:ring-green-500"
              />
              <span className="text-sm font-semibold text-gray-700">Active</span>
            </label>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 justify-end pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-lg hover:from-orange-700 hover:to-orange-800 transition-all font-medium shadow-md hover:shadow-lg"
            >
              {mode === 'create' ? 'Create Question' : 'Update Question'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HistoryQuestionsModal;
